import React from "react";
import { ArrowUpRight } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_afad2a6d-7dba-488f-b2fd-a41c3f20dfe4/artifacts/je6jqtet_logo%20bpo%20solutions%20eood%202026%20round.jpg";

export default function Footer() {
    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-white border-t border-gray-200 pt-20 pb-10"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt="BPO Solutions"
                                className="h-12 w-12 rounded-full ring-1 ring-gray-200"
                            />
                            <div className="leading-tight">
                                <div className="font-display text-lg font-extrabold tracking-tight text-bpo-ink">
                                    bpo solutions
                                    <span className="text-bpo-cyan">.</span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">
                                    Business Performance Outlook
                                </div>
                            </div>
                        </div>
                        <p className="mt-5 text-sm text-bpo-charcoal/75 max-w-md leading-relaxed">
                            Management consulting built on ISO 20700, certified
                            Black Belt, Prosci, PMP, Vested and COPC standards —
                            enabling sustainable, inclusive business growth for
                            startups, scaleups and SMBs across the EU.
                        </p>

                        <a
                            href="https://app.kadonetworks.com/kado/bposolutions"
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="footer-kado-link"
                            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-bpo-ink hover:text-bpo-cyanDark transition-colors"
                        >
                            View digital business card
                            <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="eyebrow text-bpo-charcoal mb-4">
                            Navigate
                        </div>
                        <ul className="space-y-3 text-sm">
                            {[
                                ["Services", "#services"],
                                ["Approach", "#approach"],
                                ["Who we serve", "#who-we-serve"],
                                ["Standards", "#standards"],
                                ["Contact", "#contact"],
                            ].map(([label, href]) => (
                                <li key={href}>
                                    <a
                                        href={href}
                                        data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="text-bpo-charcoal hover:text-bpo-cyanDark transition-colors"
                                    >
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="eyebrow text-bpo-charcoal mb-4">
                            Entity & Registration
                        </div>
                        <dl
                            data-testid="footer-entity-block"
                            className="space-y-3 text-sm"
                        >
                            <div>
                                <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                    Legal entity
                                </dt>
                                <dd className="text-bpo-ink font-semibold">
                                    Business Performance Outlook | BPO Solutions
                                    EOOD
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                    Registered address (not for visits)
                                </dt>
                                <dd className="text-bpo-charcoal/85">
                                    William Gladstone 30, fl. 2 · Triaditsa
                                    District 1000 · Sofia, Bulgaria
                                </dd>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                        EU TAX ID
                                    </dt>
                                    <dd className="text-bpo-ink font-semibold">
                                        BG 206101677
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                        UIC
                                    </dt>
                                    <dd className="text-bpo-ink font-semibold">
                                        206101677
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                        D-U-N-S® Number
                                    </dt>
                                    <dd className="text-bpo-ink font-semibold">
                                        525487959
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                        Phone
                                    </dt>
                                    <dd className="text-bpo-ink font-semibold">
                                        +31 6155 327 12
                                    </dd>
                                </div>
                            </div>
                            <div>
                                <dt className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
                                    Email
                                </dt>
                                <dd>
                                    <a
                                        href="mailto:bposolutions.info@gmail.com"
                                        className="text-bpo-ink font-semibold hover:text-bpo-cyanDark transition-colors break-all"
                                    >
                                        bposolutions.info@gmail.com
                                    </a>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <div className="dashed-divider mt-14 opacity-30" />

                <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-gray-500">
                    <p>
                        © {year} Business Performance Outlook | BPO Solutions
                        EOOD. All rights reserved.
                    </p>
                    <p className="uppercase tracking-[0.18em] font-semibold">
                        Remote-first · EU registered · Sofia, Bulgaria
                    </p>
                </div>
            </div>
        </footer>
    );
}
